import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db6gi34be24agvuu3jfg";

const HIGHLIGHTS = [
  { icon: "ri-live-line", title: "高清赛事入口", desc: "覆盖常规赛与季后赛的NBA赛事，清晰呈现对阵、比分与赛况。" },
  { icon: "ri-time-line", title: "实时赛程比分", desc: "赛程与比分实时更新，帮助球迷随时掌握每一场对决进度。" },
  { icon: "ri-newspaper-line", title: "丰富赛事资讯", desc: "战报、数据、花边一网打尽，满足篮球爱好者的多元化需求。" },
  { icon: "ri-device-line", title: "多设备适配", desc: "手机、平板、电脑流畅浏览，随时随地关注你支持的球队。" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于 nba直播</h1>
        <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground-500">
          nba直播是一个聚焦NBA篮球赛事的体育直播门户，致力于为篮球爱好者提供高清、实时、权威的赛事观看入口与资讯服务。
        </p>
      </header>

      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位与宗旨</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              我们面向广泛的篮球及NBA爱好者，希望通过清晰的信息组织与流畅的浏览体验，帮助用户便捷地找到感兴趣的比赛，
              实时获取赛程、比分与赛况，并通过新闻资讯了解联盟动态。网站坚持内容真实、信息易读、体验友好的原则，
              努力成为篮球迷日常关注赛事的好帮手。
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {HIGHLIGHTS.map((item) => (
                <div key={item.title} className="flex gap-3 rounded-md bg-background-100 p-4">
                  <span className="flex w-9 h-9 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
            <p className="mt-3 text-sm text-foreground-600">
              如有合作、反馈或建议，欢迎通过以下方式联系我们：
            </p>
            <a
              href="mailto:service@nba-live.example.com"
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 whitespace-nowrap transition-colors hover:bg-primary-100"
            >
              <i className="ri-mail-line" /> service@nba-live.example.com
            </a>
            <p className="mt-3 text-xs text-foreground-500">服务时间：周一至周日 全天在线</p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950">免责声明与版权</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              本站为前端演示项目，页面中的所有赛事、比分、球队、球员与新闻数据均为虚构内容，仅供界面与功能演示使用，
              不构成任何真实的赛事信息或直播服务。播放区域为界面模拟，不包含任何真实视频流。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              站内文字、图形与排版为演示内容，如涉及第三方权利，请相关权利人联系我们处理。未经许可，请勿将本站内容用于商业用途。
            </p>
            <p className="mt-3 text-xs text-foreground-500">© {new Date().getFullYear()} nba直播 · 演示项目</p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950">意见反馈</h2>
          <p className="mt-2 text-sm text-foreground-500">你的每一条建议都会帮助我们做得更好。</p>

          {status === "success" ? (
            <div className="mt-5 flex flex-col items-center rounded-md border border-secondary-200 bg-secondary-50 px-4 py-8 text-center">
              <i className="ri-checkbox-circle-line text-4xl text-secondary-600" />
              <p className="mt-3 text-sm font-semibold text-foreground-900">感谢你的反馈！</p>
              <p className="mt-1 text-xs text-foreground-600">我们已经收到你的留言，会尽快查看。</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-4 text-xs font-semibold text-primary-600 hover:text-primary-700"
              >
                再提交一条
              </button>
            </div>
          ) : (
            <form data-readdy-form id="about-feedback-form" className="mt-4 flex flex-col gap-3" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="fb-name" className="text-xs font-medium text-foreground-600">称呼</label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="fb-email" className="text-xs font-medium text-foreground-600">邮箱</label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复的邮箱"
                  className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="fb-topic" className="text-xs font-medium text-foreground-600">反馈类型</label>
                <select
                  id="fb-topic"
                  name="topic"
                  className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option>功能建议</option>
                  <option>内容纠错</option>
                  <option>合作洽谈</option>
                  <option>其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="text-xs font-medium text-foreground-600">留言内容</label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的建议或问题（最多500字）"
                  className="mt-1 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>

              <div className="aux-note-field" aria-hidden="true">
                <label htmlFor="fb-website-alt">Website</label>
                <input
                  id="fb-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "error" && formError && (
                <p className="flex items-center gap-1.5 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
                  <i className="ri-error-warning-line" /> {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
              <p className="text-[11px] text-foreground-400">我们仅将你的留言用于回复与改进服务。</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}